import React, { useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';
import moment from 'moment';
import { View } from 'react-native';
import Toast from 'react-native-root-toast';
import { Text } from '../../../components';
import { Alert } from '../../../components/other/Alert';
import { Tag } from '../../../design/components';
import { useThemedStyles } from '../../../hooks/useThemedStyles';

import { getServiceRunPicklistCalculatedTime } from '../../../../common/utils/serviceRuns/getServiceRunPicklistCalculatedTime';
import {
	RECALCULATE_UNFINISHED_PACKS_WARNING,
	RECALCULATE_AND_TOP_UP_ALL_WARNING,
} from '../../../../common/constants/text-constants';
import { RECALCULATE_SERVICE_RUN_PICKLISTS } from '../../../../common/graphql/serviceRun';
import { useUserContext } from '../../../../common/utils/context';
import { PICK_LIST_RECALCULATION_MODE, ROLE_PERMISSIONS } from '../../../../common/constants';
import { formatDate } from '../../../../common/utils/utils';

import { Button, TypographyHeading, Separator } from '../../../components';

const ServiceRunPicklistDetail = ({ servicerun, styles, refetch }) => {
	const navigation = useNavigation();
	const localStyles = useThemedStyles(createStyles);
	const { hasPermission } = useUserContext();
	const canEditPickLists = hasPermission(ROLE_PERMISSIONS.ADD_EDIT_PICK_LISTS);

	const { serviceDockets } = servicerun;

	const [recalculatingMode, setRecalculatingMode] = useState(null);
	const [recalculateServiceRunPicklists] = useMutation(RECALCULATE_SERVICE_RUN_PICKLISTS, {
		variables: { serviceRunId: servicerun.id },
	});

	const pickListCalculatedDate = useMemo(() => getServiceRunPicklistCalculatedTime(serviceDockets), [serviceDockets]);
	const pickListCalculatedWithin24h = pickListCalculatedDate && pickListCalculatedDate > moment().subtract(1, 'days');
	const hasEligiblePickListItems = useMemo(() => serviceDockets.some(docket => (
		docket.pickList?.id && Number(docket.totalPickItemsQuantity) > 0
	)), [serviceDockets]);

	const openPickListSummary = () => navigation.push('PickListSummaryView', { id: servicerun.id });

	const runPickListRecalculation = async (mode) => {
		setRecalculatingMode(mode);
		try {
			await recalculateServiceRunPicklists({ variables: { mode } });
			await refetch();
		}
		catch (error) {
			Toast.show(error?.message || 'Unable to recalculate packs');
		}
		finally {
			setRecalculatingMode(null);
		}
	};

	const openRecalculatePicklistAlert = (mode) => {
		if (!canEditPickLists || recalculatingMode) {
			return;
		}
		const isTopUpAll = mode === PICK_LIST_RECALCULATION_MODE.TOP_UP_ALL;
		const actionLabel = isTopUpAll
			? 'Recalculate and top up all'
			: 'Recalculate unfinished packs';

		Alert.alert(
			actionLabel,
			isTopUpAll
				? RECALCULATE_AND_TOP_UP_ALL_WARNING
				: RECALCULATE_UNFINISHED_PACKS_WARNING,
			[
				{ text: 'Cancel', onPress: () => {}, style: 'cancel' },
				{
					text: actionLabel,
					onPress: () => runPickListRecalculation(mode),
				},
			],
			{ cancelable: false },
		);
	};

	return (
		<>
			<Separator style={styles.separator} />
			<Text style={styles.headerText}>Picklist</Text>
			<View style={styles.detailView}>
				{
					pickListCalculatedDate && (
						<View style={localStyles.calculated}>
							<TypographyHeading
								subheadingSmallSubtle={true}
								label="PICKLIST CALCULATED"
								style={localStyles.calculatedHeading}
							/>
							<View style={localStyles.calculatedRow}>
								<Tag
									tone={pickListCalculatedWithin24h ? 'success' : 'danger'}
									label={pickListCalculatedDate ? formatDate(pickListCalculatedDate, 'DAY_MONTHWORD_YEAR_HOUR_MINUTE_AMPM') : 'n/a'}
								/>
								<Text style={localStyles.calculatedAgo}>
									{ pickListCalculatedDate && `(${pickListCalculatedDate.fromNow()})` }
								</Text>
							</View>
						</View>
					)
				}
				<View style={[styles.nthRow, localStyles.stackedButtons]}>
					<Button
						label="Recalculate unfinished packs"
						style={localStyles.stackedButton}
						onPress={() => openRecalculatePicklistAlert(PICK_LIST_RECALCULATION_MODE.UNFINISHED)}
						loading={recalculatingMode === PICK_LIST_RECALCULATION_MODE.UNFINISHED}
						disabled={!canEditPickLists || Boolean(recalculatingMode)}
					/>
					<Button
						label="Recalculate and top up all"
						style={localStyles.stackedButton}
						onPress={() => openRecalculatePicklistAlert(PICK_LIST_RECALCULATION_MODE.TOP_UP_ALL)}
						loading={recalculatingMode === PICK_LIST_RECALCULATION_MODE.TOP_UP_ALL}
						disabled={!canEditPickLists || Boolean(recalculatingMode)}
					/>
				</View>
				{
					hasEligiblePickListItems && (
						<View style={styles.nthRow}>
							<Button
								label="Pick list summary"
								onPress={openPickListSummary}
								style={localStyles.fullWidthButton}
								secondary={true}
							/>
						</View>
					)
				}
			</View>
		</>
	);
};

const createStyles = ({ ui: { colors, type, space } }) => ({
	calculated: {
		marginBottom: space.xs,
	},
	calculatedHeading: {
		marginTop: 0,
	},
	calculatedRow: {
		flexDirection: 'row',
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: space.sm,
		marginTop: space.xs,
	},
	calculatedAgo: {
		...type.caption,
		color: colors.textSecondary,
	},
	stackedButtons: {
		flexDirection: 'column',
		gap: space.sm,
	},
	stackedButton: {
		alignSelf: 'stretch',
	},
	fullWidthButton: {
		flex: 1,
	},
});

export default ServiceRunPicklistDetail;
